import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, Lock, Check, UserPlus, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

import { BACKEND_URL_EXPORT as BACKEND_URL } from '../../config';

const ReservationForm = ({ vehicle, onClose }) => {
  const { customer, isAuthenticated, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    preferred_payment_method: 'bank_transfer',
    additional_notes: ''
  });

  // Auto-submit if customer is logged in
  useEffect(() => {
    if (isAuthenticated() && customer) {
      handleAutoSubmit();
    }
  }, []);

  const handleAutoSubmit = async () => {
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/reserve-vehicle`, {
        vehicle_id: vehicle.id,
        vehicle_stock_no: vehicle.stock_no,
        vehicle_model: `${vehicle.year} ${vehicle.brand || ''} ${vehicle.model}`,
        vehicle_price_usd: vehicle.public_price_usd,
        full_name: customer.full_name,
        email: customer.email,
        phone: customer.phone || '',
        country: customer.country || '',
        city: '',
        preferred_payment_method: 'bank_transfer',
        additional_notes: ''
      }, { headers: getAuthHeaders() });

      setFormData({ ...formData, email: customer.email, full_name: customer.full_name });
      setSubmitted(true);
      toast.success('Reservation request submitted successfully!');
    } catch (error) {
      console.error('Reservation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit reservation.');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/public/reserve-vehicle`, {
        vehicle_id: vehicle.id,
        vehicle_stock_no: vehicle.stock_no,
        vehicle_model: `${vehicle.year} ${vehicle.brand ||''} ${vehicle.model}`,
        vehicle_price_usd: vehicle.public_price_usd,
        ...formData
      });

      setSubmitted(true);
      toast.success('Reservation request submitted successfully!');
    } catch (error) {
      console.error('Reservation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit reservation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
        <Card className="w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Reservation Submitted!</h2>
            <p className="text-gray-600 mb-6">
              Thank you for your interest. We've received your reservation request and will contact you within 24 hours to confirm details and provide payment instructions.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              A confirmation email has been sent to <strong>{formData.email}</strong>
            </p>
            <Button onClick={onClose} className="w-full bg-red-600 hover:bg-red-700">
              Close
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show login prompt if not authenticated
  if (!isAuthenticated()) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
        <Card className="w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
          <CardHeader className="relative">
            <button
              onClick={onClose}
              className="absolute right-4 top-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
            <CardTitle className="flex items-center space-x-2 pr-10">
              <Lock className="h-5 w-5 text-red-600" />
              <span>Reserve This Vehicle</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center py-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <UserPlus className="h-8 w-8 text-red-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Create Account to Reserve</h3>
            <p className="text-gray-600 mb-6">
              Please create an account or login to reserve this vehicle. Track your reservations and orders all in one place!
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => {
                  onClose();
                  navigate('/customer/register');
                }}
                className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Create Account
              </Button>
              <Button
                onClick={() => {
                  onClose();
                  navigate('/customer/login');
                }}
                variant="outline"
                className="border-red-600 text-red-600 hover:bg-red-50"
              >
                Login to Existing Account
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show loading state while auto-submitting
  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
        <Card className="w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
          <CardContent className="p-12 text-center">
            <Loader2 className="h-16 w-16 text-red-600 mx-auto mb-4 animate-spin" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Submitting Reservation...</h3>
            <p className="text-gray-600">Please wait while we process your request.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <Card className="w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <CardHeader className="relative">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
          <CardTitle className="flex items-center space-x-2 pr-10">
            <Lock className="h-5 w-5 text-red-600" />
            <span>Reserve This Vehicle</span>
          </CardTitle>
          <p className="text-sm text-gray-500 mt-2">
            Complete this form to reserve the vehicle. Our team will contact you within 24 hours.
          </p>
        </CardHeader>

        <CardContent>
          <div className="mb-6 p-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-lg">
            <h4 className="font-semibold text-red-900 mb-2 text-sm">
              💡 Track Your Reservation & Orders
            </h4>
            <p className="text-xs text-red-800 mb-3">
              Create a free account to track your reservations, orders, and get personalized service!
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                onClick={() => window.location.href = '/customer/register'}
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-xs h-8"
              >
                Create Account
              </Button>
              <Button
                type="button"
                onClick={() => window.location.href = '/customer/login'}
                variant="outline"
                size="sm"
                className="border-red-600 text-red-600 hover:bg-red-50 text-xs h-8"
              >
                Login
              </Button>
            </div>
          </div>

          {/* Vehicle Info Summary */}
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">
                  {vehicle.year} {vehicle.brand || ''} {vehicle.model}
                </h3>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-red-600">
                  ${vehicle.public_price_usd?.toLocaleString()}
                </p>
                <Badge className="bg-green-600 mt-1">Available</Badge>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Personal Information */}
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-900">Personal Information</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="full_name"
                  required
                  value={formData.full_name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="John Doe"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="+1 234 567 8900"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="country"
                    required
                    value={formData.country}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="United States"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="New York"
                  />
                </div>
              </div>
            </div>

            {/* Payment Preference */}
            <div className="space-y-2 pt-4 border-t">
              <h4 className="font-semibold text-gray-900">Payment Preference</h4>
              <select
                name="preferred_payment_method"
                value={formData.preferred_payment_method}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="bank_transfer">Bank Transfer (TT/Wire)</option>
                <option value="paypal">PayPal</option>
                <option value="other">Other (Will discuss)</option>
              </select>
            </div>

            {/* Additional Notes */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Additional Notes (Optional)
              </label>
              <textarea
                name="additional_notes"
                value={formData.additional_notes}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder="Any specific questions or requirements..."
              />
            </div>

            {/* Important Info */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h5 className="font-semibold text-sm text-gray-900 mb-2">Important Information:</h5>
              <ul className="text-xs text-gray-600 space-y-1 list-disc list-inside">
                <li>A reservation fee may be required to secure the vehicle</li>
                <li>Vehicle will be held for 7 days pending confirmation</li>
                <li>Final payment details will be provided via email</li>
                <li>All prices are FOB Japan (Free on Board)</li>
              </ul>
            </div>

            {/* Submit Button */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-red-600 hover:bg-red-700"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <Lock className="h-4 w-4 mr-2" />
                    Submit Reservation
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReservationForm;
